// src/state/RuntimeStateProvider.jsx
import React, { createContext, useContext, useEffect, useMemo, useReducer, useState } from "react";

import { readDeviceSnapshot, deviceReducer } from "./deviceState";
import { readTimeSnapshot, timeReducer, nowServerUtcMs } from "./timeState";
import { readLanguageSnapshot, languageReducer, DEFAULT_SUPPORTED_LANGS } from "./languageState";
import { readSoundSnapshot, soundReducer } from "./soundState";
import {
  PLAY_SETTINGS_CHANGED_EVENT,
  PLAY_SETTINGS_STORAGE_KEY,
  readPlaySettingsSnapshot,
  playSettingsReducer,
} from "./playSettingsState";

// ======================================================
// Context
// ======================================================
// 기능별 Context를 분리해 time tick(기본 1초)이 playSettings/device/lang/sound 소비자까지
// 전부 리렌더시키지 않도록 한다. useRuntimeState() 호환 API는 그대로 유지한다.
const RuntimeStateContext = createContext(null);
const DeviceStateContext = createContext(null);
const TimeStateContext = createContext(null);
const LanguageStateContext = createContext(null);
const SoundStateContext = createContext(null);
const PlaySettingsStateContext = createContext(null);

function useRequiredContext(Context, name) {
  const ctx = useContext(Context);
  if (!ctx) throw new Error(`${name} must be used within RuntimeStateProvider`);
  return ctx;
}

// ======================================================
// Provider
// ======================================================
export function RuntimeStateProvider({
  children,
  supportedLangs = DEFAULT_SUPPORTED_LANGS,
  nowTickMs = 1000, // "현재 시간" UI를 위한 tick (state에 now를 저장하지 않음)
}) {
  const [device, deviceDispatch] = useReducer(deviceReducer, undefined, () => readDeviceSnapshot());
  const [time, timeDispatch] = useReducer(timeReducer, undefined, () => readTimeSnapshot());
  const [lang, langDispatch] = useReducer(languageReducer, undefined, () => readLanguageSnapshot(supportedLangs));
  const [sound, soundDispatch] = useReducer(soundReducer, undefined, () => readSoundSnapshot());
  const [playSettings, playSettingsDispatch] = useReducer(playSettingsReducer, undefined, () => readPlaySettingsSnapshot());

  // 디바이스 스냅샷 갱신 (resize/orientationchange)
  useEffect(() => {
    const onResize = () => {
      deviceDispatch({ type: "DEVICE/REFRESH", payload: readDeviceSnapshot() });
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);

  // tz/locale 메타는 가끔 갱신(탭 장시간 켜두면 환경 바뀌는 케이스 방어)
  useEffect(() => {
    const updateMeta = () => {
      timeDispatch({
        type: "TIME/REFRESH_META",
        payload: {
          tzOffsetMin: new Date().getTimezoneOffset(),
          locale: (navigator?.language || "en"),
        },
      });
    };
    updateMeta();
  }, []);

  // 로비 설정 변경을 같은 창/팝업 테이블 창 모두에 반영한다.
  useEffect(() => {
    const refreshPlaySettings = () => {
      playSettingsDispatch({ type: "PLAY_SETTINGS/REFRESH" });
    };
    const onStorage = (event) => {
      if (event?.key && event.key !== PLAY_SETTINGS_STORAGE_KEY) return;
      refreshPlaySettings();
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener(PLAY_SETTINGS_CHANGED_EVENT, refreshPlaySettings);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(PLAY_SETTINGS_CHANGED_EVENT, refreshPlaySettings);
    };
  }, []);

  const deviceApi = useMemo(() => ({
    device,
    refreshDevice: () => deviceDispatch({ type: "DEVICE/REFRESH", payload: readDeviceSnapshot() }),
  }), [device]);

  const timeApi = useMemo(() => ({
    time,
    nowTickMs,
    syncServerUtc: (serverUtcIsoOrMs) => timeDispatch({ type: "TIME/SERVER_SYNC", payload: serverUtcIsoOrMs }),
    nowServerUtcMs: () => nowServerUtcMs(time),
    nowLocalDate: () => new Date(nowServerUtcMs(time)),
  }), [time, nowTickMs]);

  const languageApi = useMemo(() => ({
    lang,
    setLanguage: (nextLang) => langDispatch({ type: "LANG/SET", payload: nextLang }),
    setLanguageFromServer: (nextLang) => langDispatch({ type: "LANG/SET_FROM_SERVER", payload: nextLang }),
  }), [lang]);

  const soundApi = useMemo(() => ({
    sound,
    setMasterVolume: (v) => soundDispatch({ type: "SOUND/SET_MASTER", payload: v }),
    setMuted: (m) => soundDispatch({ type: "SOUND/SET_MUTED", payload: m }),
    setBgmVolume: (v) => soundDispatch({ type: "SOUND/SET_BGM", payload: v }),
    setSfxVolume: (v) => soundDispatch({ type: "SOUND/SET_SFX", payload: v }),
  }), [sound]);

  const playSettingsApi = useMemo(() => ({
    playSettings,
    setMuckEnabled: (v) => playSettingsDispatch({ type: "PLAY_SETTINGS/SET_MUCK_ENABLED", payload: v }),
    setAutoRebuyEnabled: (v) => playSettingsDispatch({ type: "PLAY_SETTINGS/SET_AUTO_REBUY_ENABLED", payload: v }),
    setAutoRebuyBb: (v) => playSettingsDispatch({ type: "PLAY_SETTINGS/SET_AUTO_REBUY_BB", payload: v }),
  }), [playSettings]);

  // 기존 useRuntimeState() 사용처를 위한 통합 API. 세부 hook은 아래 개별 Context를 직접 읽는다.
  const api = useMemo(() => ({
    ...deviceApi,
    ...timeApi,
    ...languageApi,
    ...soundApi,
    ...playSettingsApi,
  }), [deviceApi, timeApi, languageApi, soundApi, playSettingsApi]);

  return (
    <RuntimeStateContext.Provider value={api}>
      <DeviceStateContext.Provider value={deviceApi}>
        <TimeStateContext.Provider value={timeApi}>
          <LanguageStateContext.Provider value={languageApi}>
            <SoundStateContext.Provider value={soundApi}>
              <PlaySettingsStateContext.Provider value={playSettingsApi}>
                {children}
              </PlaySettingsStateContext.Provider>
            </SoundStateContext.Provider>
          </LanguageStateContext.Provider>
        </TimeStateContext.Provider>
      </DeviceStateContext.Provider>
    </RuntimeStateContext.Provider>
  );
}

// ======================================================
// Hooks
// ======================================================
function useConsumerTick(intervalMs) {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const delay = Number(intervalMs || 0);
    if (!Number.isFinite(delay) || delay <= 0) return undefined;

    let timer = null;
    const stop = () => {
      if (timer) clearInterval(timer);
      timer = null;
    };
    const start = () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        stop();
        return;
      }
      setTick((x) => x + 1);
      if (!timer) timer = setInterval(() => setTick((x) => x + 1), delay);
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") start();
      else stop();
    };

    start();
    if (typeof document !== "undefined") document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      if (typeof document !== "undefined") document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [intervalMs]);

  return tick;
}

export function useRuntimeState() {
  const api = useRequiredContext(RuntimeStateContext, "useRuntimeState");
  const tick = useConsumerTick(api.nowTickMs);
  return useMemo(() => ({ ...api, tick }), [api, tick]);
}

export function useDeviceState() {
  return useRequiredContext(DeviceStateContext, "useDeviceState").device;
}

export function useTimeState() {
  const { time, nowTickMs, nowLocalDate, nowServerUtcMs: getNowServerUtcMs } = useRequiredContext(TimeStateContext, "useTimeState");
  const tick = useConsumerTick(nowTickMs);
  return { time, tick, nowLocalDate, nowServerUtcMs: getNowServerUtcMs };
}

export function useLanguageState() {
  const { lang, setLanguage } = useRequiredContext(LanguageStateContext, "useLanguageState");
  return { lang, setLanguage };
}

export function useSoundState() {
  const { sound, setMasterVolume, setMuted, setBgmVolume, setSfxVolume } = useRequiredContext(SoundStateContext, "useSoundState");
  return { sound, setMasterVolume, setMuted, setBgmVolume, setSfxVolume };
}

export function usePlaySettingsState() {
  const { playSettings, setMuckEnabled, setAutoRebuyEnabled, setAutoRebuyBb } = useRequiredContext(PlaySettingsStateContext, "usePlaySettingsState");
  return { playSettings, setMuckEnabled, setAutoRebuyEnabled, setAutoRebuyBb };
}
